import moment from "moment";
import { Plus } from "lucide-react";
import Button from "../../components/Button";
import DateTimeRange from "../../components/DateTimeRange.jsx";
import LevelCrossing from "./LevelCrossing";
import MarkerBoard from "./MarkerBoard";
import Worksite from "./Worksite";
import { datesMatch, shouldUpdateLinkedDate } from "../../../Helpers/linkedDates";

const operationalStatuses = [
  ["N", "Normal working at the crossing"],
  ["A", "An attendant is required throughout"],
  ["E", "Road signals, sirens or bells are switched off"],
  ["Q", "An attendant is required some of the time"],
  ["W", "Wrong-direction movements must be cautioned or stopped"],
  ["C", "All movements must be cautioned"],
];

const toLocal = (value) => value && moment(value).isValid() ? moment(value).format("YYYY-MM-DDTHH:mm") : "";
const toStored = (value) => value && moment(value).isValid() ? moment(value).toISOString() : value;
const firstError = (errors, key) => {
  const value = Array.isArray(errors?.[key]) ? errors[key][0] : errors?.[key];
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value?.message === "string") return value.message;
  return "Enter a valid planned start and end date.";
};

const Possession = (props) => {
  const data = props.possessionData || {};
  const index = props.possessionIndex || 0;
  const path = `possessions.${index}`;
  const worksiteTemplate = props.worksiteTemplate || {};
  const crossingTemplate = props.levelCrossingTemplate || {};
  const markerTemplate = props.markerBoardTemplate || {};
  const rangeError = firstError(props.errors, `data.possessions.${index}.planned_start_date`) ||
    firstError(props.errors, `data.possessions.${index}.planned_end_date`) ||
    props.aggregateDateError || "";
  const changePeriod = ({ start, end }) => {
    const storedStart = toStored(start);
    const storedEnd = toStored(end);
    const possessions = props.values?.possessions || [];
    const esUsers = props.values?.users?.esusers || [];

    if (!datesMatch(storedStart, data.planned_start_date)) {
      props.handleInputChange(`${path}.planned_start_date`, storedStart);
      if (index === 0 && esUsers[0] && shouldUpdateLinkedDate(esUsers[0].start_date, data.planned_start_date)) {
        props.handleInputChange("users.esusers.0.start_date", storedStart);
      }
    }

    if (!datesMatch(storedEnd, data.planned_end_date)) {
      props.handleInputChange(`${path}.planned_end_date`, storedEnd);
      const followingPossession = possessions[index + 1];
      if (followingPossession && shouldUpdateLinkedDate(followingPossession.planned_start_date, data.planned_end_date)) {
        props.handleInputChange(`possessions.${index + 1}.planned_start_date`, storedEnd);
      }
      if (index === possessions.length - 1 && esUsers.length > 0) {
        const finalEsIndex = esUsers.length - 1;
        const previousPackEnd = data.planned_end_date || possessions[index - 1]?.planned_end_date;
        if (shouldUpdateLinkedDate(esUsers[finalEsIndex].end_date, previousPackEnd)) {
          props.handleInputChange(`users.esusers.${finalEsIndex}.end_date`, storedEnd);
        }
      }
    }
  };

  return (
    <div className="wsm-possession-editor">
      <DateTimeRange
        end={toLocal(data.planned_end_date)}
        endLabel="Planned end"
        error={rangeError}
        helpText="The end must be later than the start. Times use the local operational timezone."
        id={`possession-${index + 1}-period`}
        label={`Part ${index + 1} period`}
        onChange={changePeriod}
        required
        start={toLocal(data.planned_start_date)}
        startLabel="Planned start"
      />

      <section className="wsm-worksite-list">
        <div className="wsm-worksite-list__heading">
          <div><h4>Worksites</h4><p>Add the worksite references covered by this part.</p></div>
          <Button icon={Plus} onClick={() => props.handleAddFormRow(`${path}.worksites`, worksiteTemplate)} size="compact" variant="outline">Add worksite</Button>
        </div>
        <div className="wsm-worksite-list__items">
          {(data.worksites || []).map((worksite, worksiteIndex) => (
            <Worksite
              activeElrs={props.activeElrs}
              conversionUnit={props.conversionUnit}
              elrsLoading={props.elrsLoading}
              errors={props.errors}
              handleAddFormRow={props.handleAddFormRow}
              handleInputChange={props.handleInputChange}
              handleRemoveFormRow={props.handleRemoveFormRow}
              key={`possession-${index}-worksite-${worksiteIndex}`}
              linesAtSiteTemplate={worksiteTemplate.lines_at_site?.[0] || {}}
              possessionData={data}
              possessionIndex={index}
              setValues={props.setValues}
              values={props.values}
              worksiteData={worksite}
              worksiteIndex={worksiteIndex}
            />
          ))}
        </div>
      </section>

      <section className="wsm-possession-related">
        <div className="wsm-possession-editor__subheading">
          <div><h4>Level crossings</h4><p>Record any crossings affected by the possession.</p></div>
          <Button icon={Plus} onClick={() => props.handleAddFormRow(`${path}.level_crossings`, crossingTemplate)} size="compact" variant="outline">Add level crossing</Button>
        </div>
        <div className="wsm-inline-editor-list">
          {(data.level_crossings || []).map((crossing, crossingIndex) => <LevelCrossing {...props} key={`crossing-${crossingIndex}`} levelCrossingData={crossing} levelCrossingIndex={crossingIndex} possessionData={data} possessionIndex={index} />)}
        </div>
        <div className="wsm-status-guide">
          <dl>{operationalStatuses.map(([code, description]) => <div key={code}><dt>{code}</dt><dd>{description}</dd></div>)}</dl>
        </div>
      </section>

      <section className="wsm-possession-related">
        <div className="wsm-possession-editor__subheading">
          <div><h4>Marker boards</h4><p>Record the marker boards required for this worksite.</p></div>
          <Button icon={Plus} onClick={() => props.handleAddFormRow(`${path}.marker_boards`, markerTemplate)} size="compact" variant="outline">Add marker board</Button>
        </div>
        <div className="wsm-inline-editor-list">
          {(data.marker_boards || []).map((board, boardIndex) => <MarkerBoard {...props} key={`marker-${boardIndex}`} markerBoardData={board} markerBoardIndex={boardIndex} possessionData={data} possessionIndex={index} />)}
        </div>
      </section>
    </div>
  );
};

export default Possession;
